import React from 'react';
import { useLocalization } from '@/contexts/LocalizationContext';

export const RequestFooter = () => {
  const [openSection, setOpenSection] = React.useState<string | null>(null);
  const { t, locale } = useLocalization();

  const isRTL = locale === 'ar';

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  const copyrightText = t('footer.copyright').replace(
    '{year}',
    new Date().getFullYear().toString()
  );

  return (
    <footer className="w-full bg-white border-t border-gray-300 pt-10 pb-4">
      {/* Mobile Layout */}
      <div className="lg:hidden max-w-[1300px] mx-auto px-6 flex flex-col items-center gap-6 text-center">
        {/* Logo and Description */}
        <div className="flex flex-col items-center gap-3">
          <a
            href="#top"
            className="cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            <img
              src="/assets/footer/Form Page - Logo & Icons-02.svg"
              alt="WTDcare Logo"
              className="h-16 w-auto hover:opacity-80 transition-opacity"
            />
          </a>
          <p className="text-sm text-[#000000] leading-relaxed max-w-md">
            {t('footer.description')}
          </p>
        </div>

        {/* Quick Links */}
        <div className="w-full border-b border-gray-200 py-3">
          <a
            href="https://wtdcare.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-lg font-semibold text-[#000000] hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors"
          >
            {t('footer.home')}
          </a>
        </div>

        {/* Policies - Collapsible */}
        <div className="w-full border-b border-gray-200">
          <button
            onClick={() => toggleSection('policies')}
            className="w-full flex items-center justify-center gap-2 py-3 text-[#000000]"
          >
            <svg
              className={`w-4 h-4 transition-transform ${openSection === 'policies' ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
            <h3 className="text-lg font-semibold">{t('footer.policiesAndTerms')}</h3>
          </button>
          {openSection === 'policies' && (
            <div className="pb-4 flex flex-wrap justify-center gap-3 text-sm">
              <a
                href="https://wtdcare.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#000000] hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors"
              >
                {t('footer.privacyPolicy')}
              </a>
              <span>•</span>
              <a
                href="https://wtdcare.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#000000] hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors"
              >
                {t('footer.termsAndConditions')}
              </a>
            </div>
          )}
        </div>

        {/* Contact Info - Collapsible */}
        <div className="w-full border-b border-gray-200">
          <button
            onClick={() => toggleSection('contact')}
            className="w-full flex items-center justify-center gap-2 py-3 text-[#000000]"
          >
            <svg
              className={`w-4 h-4 transition-transform ${openSection === 'contact' ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
            <h3 className="text-lg font-semibold">{t('footer.contactUs')}</h3>
          </button>
          {openSection === 'contact' && (
            <div className="pb-4 flex flex-col gap-2 text-sm">
              <a
                href="tel:920033297"
                className="text-[#000000] hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors"
              >
                920033297
              </a>
              <a
                href="mailto:Info@wtd.care"
                className="text-[#000000] hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors"
              >
                Info@wtd.care
              </a>
            </div>
          )}
        </div>

        {/* Social Media */}
        <div className="flex flex-col items-center gap-4 mt-4">
          <h3 className="text-lg font-semibold text-[#000000]">{t('footer.socialMedia')}</h3>
          <div className="flex items-center justify-center flex-wrap gap-2">
            <a href="https://linkedin.com/company/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
              <img src="/assets/footer/Form Page - Logo & Icons-07.svg" alt="LinkedIn" className="h-7 w-7" />
            </a>
            <a href="https://facebook.com/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
              <img src="/assets/footer/Form Page - Logo & Icons-06.svg" alt="Facebook" className="h-7 w-7" />
            </a>
            <a href="https://instagram.com/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
              <img src="/assets/footer/Form Page - Logo & Icons-05.svg" alt="Instagram" className="h-7 w-7" />
            </a>
            <a href="https://twitter.com/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
              <img src="/assets/footer/Form Page - Logo & Icons-04.svg" alt="X" className="h-7 w-7" />
            </a>
            <a href="https://tiktok.com/@wtdcare" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
              <img src="/assets/footer/Form Page - Logo & Icons-03.svg" alt="TikTok" className="h-7 w-7" />
            </a>
          </div>
          <div className="flex items-center gap-3 mt-2">
            <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Download on Google Play">
              <img src="/assets/footer/Form Page - Logo & Icons-10.svg" alt="Get it on Google Play" className="h-10 w-auto" />
            </a>
            <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Download on App Store">
              <img src="/assets/footer/Form Page - Logo & Icons-11.svg" alt="Download on App Store" className="h-10 w-auto" />
            </a>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden lg:grid max-w-[1300px] mx-auto px-6 grid-cols-5 gap-8" dir="ltr">
        {isRTL ? (
          <>
            {/* Arabic Order: Social Media | Contact Us | Policies | Quick Links | Logo */}
            <div className="flex flex-col items-start gap-4" dir="rtl">
              <h3 className="text-lg font-semibold text-[#000000]">{t('footer.socialMedia')}</h3>
              <div className="flex items-center gap-1 flex-row-reverse">
                <a href="https://wa.me/920033297" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-09.svg" alt="WhatsApp" className="h-7 w-7" />
                </a>
                <a href="https://snapchat.com/add/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="Snapchat" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-08.svg" alt="Snapchat" className="h-7 w-7" />
                </a>
                <a href="https://tiktok.com/@wtdcare" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-03.svg" alt="TikTok" className="h-7 w-7" />
                </a>
                <a href="https://twitter.com/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-04.svg" alt="X" className="h-7 w-7" />
                </a>
                <a href="https://instagram.com/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-05.svg" alt="Instagram" className="h-7 w-7" />
                </a>
                <a href="https://facebook.com/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-06.svg" alt="Facebook" className="h-7 w-7" />
                </a>
                <a href="https://linkedin.com/company/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-07.svg" alt="LinkedIn" className="h-7 w-7" />
                </a>
              </div>
              <div className="flex items-center gap-3 mt-2 flex-row-reverse">
                <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Download on App Store">
                  <img src="/assets/footer/Form Page - Logo & Icons-11.svg" alt="Download on App Store" className="h-10 w-auto" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Download on Google Play">
                  <img src="/assets/footer/Form Page - Logo & Icons-10.svg" alt="Get it on Google Play" className="h-10 w-auto" />
                </a>
              </div>
            </div>

            <div className="flex flex-col gap-3 text-[#000000]" dir="rtl">
              <h3 className="text-lg font-semibold text-end">{t('footer.contactUs')}</h3>
              <a href="tel:920033297" className="text-sm text-end hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors">920033297</a>
              <a href="mailto:Info@wtd.care" className="text-sm text-end hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors">Info@wtd.care</a>
            </div>

            <div className="flex flex-col gap-3 text-[#000000]" dir="rtl">
              <h3 className="text-lg font-semibold text-end">{t('footer.policiesAndTerms')}</h3>
              <a href="https://wtdcare.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-sm text-end hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors">{t('footer.privacyPolicy')}</a>
              <a href="https://wtdcare.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-sm text-end hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors">{t('footer.termsAndConditions')}</a>
            </div>

            <div className="flex flex-col gap-3 text-[#000000]" dir="rtl">
              <h3 className="text-lg font-semibold text-end">{t('footer.quickLinks')}</h3>
              <ul className="text-sm space-y-1 text-end">
                <li>
                  <a href="https://wtdcare.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors">{t('footer.home')}</a>
                </li>
              </ul>
            </div>

            <div className="flex flex-col items-end gap-3" dir="rtl">
              <a href="#top" className="cursor-pointer" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <img src="/assets/footer/Form Page - Logo & Icons-02.svg" alt="WTDcare Logo" className="h-16 w-auto hover:opacity-80 transition-opacity" />
              </a>
              <p className="text-sm text-[#000000] leading-relaxed text-end">{t('footer.description')}</p>
            </div>
          </>
        ) : (
          <>
            {/* English Order: Logo | Quick Links | Policies | Contact Us | Social Media */}
            <div className="flex flex-col items-start text-start gap-3">
              <a href="#top" className="cursor-pointer" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <img src="/assets/footer/Form Page - Logo & Icons-02.svg" alt="WTDcare Logo" className="h-16 w-auto hover:opacity-80 transition-opacity" />
              </a>
              <p className="text-sm text-[#000000] leading-relaxed">{t('footer.description')}</p>
            </div>

            <div className="flex flex-col gap-3 text-[#000000] text-start">
              <h3 className="text-lg font-semibold">{t('footer.quickLinks')}</h3>
              <ul className="text-sm space-y-1">
                <li>
                  <a href="https://wtdcare.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors">{t('footer.home')}</a>
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-3 text-[#000000] text-start">
              <h3 className="text-lg font-semibold">{t('footer.policiesAndTerms')}</h3>
              <a href="https://wtdcare.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors">{t('footer.privacyPolicy')}</a>
              <a href="https://wtdcare.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors">{t('footer.termsAndConditions')}</a>
            </div>

            <div className="flex flex-col gap-3 text-[#000000] text-start">
              <h3 className="text-lg font-semibold">{t('footer.contactUs')}</h3>
              <a href="tel:920033297" className="text-sm hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors">920033297</a>
              <a href="mailto:Info@wtd.care" className="text-sm hover:text-[rgb(49,133,252)] active:text-[rgb(49,133,252)] transition-colors">Info@wtd.care</a>
            </div>

            <div className="flex flex-col items-end gap-4">
              <h3 className="text-lg font-semibold text-[#000000]">{t('footer.socialMedia')}</h3>
              <div className="flex items-center gap-1">
                <a href="https://linkedin.com/company/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-07.svg" alt="LinkedIn" className="h-7 w-7" />
                </a>
                <a href="https://facebook.com/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-06.svg" alt="Facebook" className="h-7 w-7" />
                </a>
                <a href="https://instagram.com/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-05.svg" alt="Instagram" className="h-7 w-7" />
                </a>
                <a href="https://twitter.com/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-04.svg" alt="X" className="h-7 w-7" />
                </a>
                <a href="https://tiktok.com/@wtdcare" target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-03.svg" alt="TikTok" className="h-7 w-7" />
                </a>
                <a href="https://snapchat.com/add/wtdcare" target="_blank" rel="noopener noreferrer" aria-label="Snapchat" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-08.svg" alt="Snapchat" className="h-7 w-7" />
                </a>
                <a href="https://wa.me/920033297" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors">
                  <img src="/assets/footer/Form Page - Logo & Icons-09.svg" alt="WhatsApp" className="h-7 w-7" />
                </a>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Download on Google Play">
                  <img src="/assets/footer/Form Page - Logo & Icons-10.svg" alt="Get it on Google Play" className="h-10 w-auto" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" aria-label="Download on App Store">
                  <img src="/assets/footer/Form Page - Logo & Icons-11.svg" alt="Download on App Store" className="h-10 w-auto" />
                </a>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bottom line and copyright */}
      <div className="max-w-[1300px] mx-auto mt-10 px-6">
        <hr className="border-gray-300" />
        <p className="text-center text-sm mt-4 text-gray-600">{copyrightText}</p>
      </div>
    </footer>
  );
};
